import { type FieldType, type FilterOp, ME_TOKEN } from '@servicetim/shared';
import { Checkbox, FieldControl, Input, type SchemaFormField } from '@servicetim/ui/components';
import { type CatalogField, isUserType } from '../catalog/field-catalog';
import { SimpleSelect } from '../ui/simple-select';
import { opShape, TEXT_OPS } from './ops';

export interface ConditionValueControlProps {
  field: CatalogField | undefined;
  op: FilterOp;
  value: unknown;
  onChange: (value: unknown) => void;
  id: string;
  disabled?: boolean;
  /** Offer the `$me` token on user fields (queries and rule conditions). */
  allowMe?: boolean;
  /** Extra tokens offered instead of a literal value (`$now`, `$current_user` for `set_field`). */
  tokens?: readonly string[];
}

/** The control type a scalar value of `field` needs under `op`. */
function scalarType(field: CatalogField, op: FilterOp): FieldType {
  if (field.type === 'text_search' || TEXT_OPS.has(op)) return 'text';
  switch (field.type) {
    case 'multi_select':
      return 'single_select';
    case 'multi_user':
      return 'user';
    case 'tags':
      return 'text';
    default:
      return field.type;
  }
}

function controlField(field: CatalogField, type: FieldType): SchemaFormField {
  return {
    key: field.key,
    type,
    label: field.label,
    required: false,
    readOnly: false,
    system: field.system,
    options: field.options,
    config: field.config,
  };
}

const BOOLEAN_OPTIONS = [
  { value: 'true', label: 'True' },
  { value: 'false', label: 'False' },
];

/**
 * Value editor for one `{ field, op, value }` leaf: nothing for `is_null`, two inputs for
 * `between`, a multi-value control for `in`/`any_of`, otherwise the field's own control. User
 * fields can take `$me`; post-functions can take `$now` / `$current_user` tokens.
 */
export function ConditionValueControl({
  field,
  op,
  value,
  onChange,
  id,
  disabled,
  allowMe = true,
  tokens = [],
}: ConditionValueControlProps) {
  const shape = opShape(op);
  if (shape === 'none') return null;
  if (!field) {
    return <Input id={id} disabled placeholder="Choose a field first" aria-label="Value" />;
  }
  const userField = isUserType(field.type);
  const tokenChoices = [...tokens, ...(allowMe && userField ? [ME_TOKEN] : [])];
  const tokenValue =
    typeof value === 'string' && tokenChoices.includes(value)
      ? value
      : Array.isArray(value)
        ? tokenChoices.find((t) => value.includes(t))
        : undefined;

  const tokenToggle =
    tokenChoices.length > 0 ? (
      <div className="flex flex-wrap items-center gap-3">
        {tokenChoices.map((token) => {
          const checked = tokenValue === token;
          const tokenId = `${id}-token-${token.replace(/[^a-z_]/gi, '')}`;
          return (
            <span key={token} className="flex items-center gap-1.5 text-fg-muted text-xs">
              <Checkbox
                id={tokenId}
                checked={checked}
                disabled={disabled}
                onCheckedChange={(next) => {
                  if (shape === 'array') {
                    const rest = (Array.isArray(value) ? value : []).filter(
                      (v) => !tokenChoices.includes(v as string),
                    );
                    onChange(next === true ? [...rest, token] : rest.length ? rest : undefined);
                  } else {
                    onChange(next === true ? token : undefined);
                  }
                }}
              />
              <label htmlFor={tokenId} className="flex items-center gap-1.5">
                <code className="font-mono">{token}</code>
                <span>{TOKEN_HINTS[token] ?? ''}</span>
              </label>
            </span>
          );
        })}
      </div>
    ) : null;

  if (shape === 'scalar' && tokenValue !== undefined) {
    return <div className="space-y-1.5">{tokenToggle}</div>;
  }
  // Controls take their accessible name from a label; the leaf row has none, so add a hidden one.
  const hiddenLabel = (controlId: string, suffix: string) => (
    <label htmlFor={controlId} className="sr-only">
      {field.label} {suffix}
    </label>
  );

  if (shape === 'range') {
    const type = scalarType(field, op);
    const pair = Array.isArray(value) ? value : [undefined, undefined];
    const set = (index: 0 | 1, next: unknown) => {
      const copy: unknown[] = [pair[0], pair[1]];
      copy[index] = next;
      onChange(copy);
    };
    return (
      <div className="flex flex-wrap items-center gap-2">
        {hiddenLabel(id, 'from')}
        <FieldControl
          field={controlField(field, type)}
          value={pair[0]}
          onChange={(next) => set(0, next)}
          id={id}
          disabled={disabled}
        />
        <span className="text-fg-muted text-xs">and</span>
        {hiddenLabel(`${id}-high`, 'to')}
        <FieldControl
          field={controlField(field, type)}
          value={pair[1]}
          onChange={(next) => set(1, next)}
          id={`${id}-high`}
          disabled={disabled}
        />
      </div>
    );
  }

  if (shape === 'array') {
    const literal = Array.isArray(value)
      ? value.filter((v) => !tokenChoices.includes(v as string))
      : [];
    const emit = (next: unknown) => {
      const list = Array.isArray(next) ? next : [];
      const withToken = tokenValue ? [...list, tokenValue] : list;
      onChange(withToken.length > 0 ? withToken : undefined);
    };
    let control: SchemaFormField;
    if (field.options) {
      control = controlField(field, 'multi_select');
    } else if (userField) {
      control = controlField(field, 'multi_user');
    } else {
      control = { ...controlField(field, 'tags'), config: undefined };
    }
    const numeric = field.type === 'number';
    return (
      <div className="space-y-1.5">
        {hiddenLabel(id, 'values')}
        <FieldControl
          field={control}
          value={numeric ? literal.map((v) => String(v)) : literal}
          onChange={(next) =>
            emit(
              numeric && Array.isArray(next)
                ? next.map((v) => Number(v)).filter((n) => !Number.isNaN(n))
                : next,
            )
          }
          id={id}
          disabled={disabled}
        />
        {tokenToggle}
      </div>
    );
  }

  const type = scalarType(field, op);
  if (type === 'boolean') {
    return (
      <SimpleSelect
        id={id}
        value={value === true ? 'true' : value === false ? 'false' : undefined}
        onChange={(next) => onChange(next === undefined ? undefined : next === 'true')}
        options={BOOLEAN_OPTIONS}
        disabled={disabled}
        aria-label={`${field.label} value`}
      />
    );
  }
  return (
    <div className="space-y-1.5">
      {hiddenLabel(id, 'value')}
      <FieldControl
        field={controlField(field, type)}
        value={value}
        onChange={onChange}
        id={id}
        disabled={disabled}
      />
      {tokenToggle}
    </div>
  );
}

const TOKEN_HINTS: Record<string, string> = {
  [ME_TOKEN]: 'current user at run time',
  $now: 'time of the transition',
  $current_user: 'user executing the transition',
};
